import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {DatePipe} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {forkJoin, Subject, takeUntil} from 'rxjs';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faBuilding, faLinkSlash, faPlus} from '@fortawesome/pro-solid-svg-icons';
import {BicBadgeComponent, BicButtonComponent} from '@buildinginformationcloud/bic-ui-components';
import {
  ConnectionSiteView,
  ExternalConnectionControllerService,
  ExternalConnectionView,
  Project,
  ProjectControllerService,
  SitePermissionSessionView
} from '../../../../api';
import {NotificationsService} from '../../../../services/notifications/notifications.service';
import {ConnectSharePointDialogComponent} from './connect-sharepoint-dialog.component';
import {SitePermissionDialogComponent} from './site-permission-dialog.component';
import {SiteProjectsDialogComponent} from './site-projects-dialog.component';
import {DisconnectDialogComponent} from './disconnect-dialog.component';
import {consentErrorText, STATE_ERROR_TEXTS} from './data-source-texts';

/**
 * Bereich „Datenquellen“ der Organisationseinstellungen (BIC-Frontend#2719): SharePoint verbinden,
 * Sites freigeben, Sites Projekten zuordnen, Verbindung trennen. Nur für Org-Admins
 * (`ORGANISATION_SETTINGS`), das prüft auch das Backend.
 *
 * Die Microsoft-Logins verlassen planBIC; das Backend leitet danach mit Query-Parametern hierher zurück
 * (`consent`, `reason`, `siteSession`, `datasourceError`). Die werden einmal ausgewertet und entfernt.
 */
@Component({
  selector: 'app-organisation-data-sources',
  templateUrl: './organisation-data-sources.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BicButtonComponent, BicBadgeComponent, FaIconComponent, DatePipe]
})
export class OrganisationDataSourcesComponent implements OnInit, OnDestroy {
  private readonly connectionService = inject(ExternalConnectionControllerService);
  private readonly projectService = inject(ProjectControllerService);
  private readonly notifications = inject(NotificationsService);
  private readonly dialog = inject(MatDialog);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input({required: true}) organisationId!: number;

  sharePoint: ExternalConnectionView | undefined;
  loading = true;
  loadError = false;
  busy = false;
  private projectsById = new Map<number, string>();
  private readonly unsubscribe = new Subject<void>();

  protected readonly faBuilding = faBuilding;
  protected readonly faLinkSlash = faLinkSlash;
  protected readonly faPlus = faPlus;

  ngOnInit(): void {
    this.load();
    this.projectService.getProjectsByOrganisationId(this.organisationId).pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: projects => {
          this.projectsById = new Map(projects.filter((p: Project) => p.id != null).map((p: Project) => [p.id!, p.name ?? '']));
          this.cdr.markForCheck();
        },
        error: () => this.notifications.showErrorMessage('Die Projekte der Organisation konnten nicht geladen werden.')
      });
    this.handleCallbackParams();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  get status(): ExternalConnectionView.StatusEnum | undefined {
    return this.sharePoint?.status;
  }

  get isActive(): boolean {
    return this.status === ExternalConnectionView.StatusEnum.ACTIVE;
  }

  /** Meta-Text einer Site: „3 Projekte“ bzw. „Keinem Projekt zugeordnet“. */
  projectSummary(site: ConnectionSiteView): string {
    const count = site.projectIds?.length ?? 0;
    if (count === 0) {
      return 'Keinem Projekt zugeordnet';
    }
    return count === 1 ? '1 Projekt' : `${count} Projekte`;
  }

  /** Projektnamen für den Tooltip, kommagetrennt. */
  projectNames(site: ConnectionSiteView): string {
    return (site.projectIds ?? []).map(id => this.projectsById.get(id) ?? `Projekt ${id}`).join(', ');
  }

  connect(): void {
    this.dialog.open(ConnectSharePointDialogComponent, {
      data: {organisationId: this.organisationId, connection: this.sharePoint},
      panelClass: 'rounded-corners-dialog'
    }).afterClosed().subscribe(() => this.load());
  }

  manageSites(): void {
    if (!this.sharePoint?.id) {
      return;
    }
    this.busy = true;
    this.connectionService.startSitePermissionSession(this.organisationId, this.sharePoint.id).subscribe({
      next: start => this.redirect(start.loginUrl!),
      error: err => this.fail(err, 'Die Freigabe konnte nicht gestartet werden.')
    });
  }

  assignProjects(site: ConnectionSiteView): void {
    this.dialog.open(SiteProjectsDialogComponent, {
      data: {organisationId: this.organisationId, connectionId: this.sharePoint!.id, site},
      panelClass: 'rounded-corners-dialog'
    }).afterClosed().subscribe(changed => {
      if (changed) {
        this.load();
      }
    });
  }

  disconnect(): void {
    this.dialog.open(DisconnectDialogComponent, {
      data: {organisationId: this.organisationId, connection: this.sharePoint},
      panelClass: 'rounded-corners-dialog'
    }).afterClosed().subscribe(result => {
      if (result === 'done') {
        this.notifications.showSuccessMessage('Die Verbindung zu SharePoint wurde getrennt.');
        this.load();
      }
    });
  }

  private load(): void {
    this.loading = true;
    this.connectionService.listExternalConnections(this.organisationId).pipe(takeUntil(this.unsubscribe)).subscribe({
      next: connections => {
        this.sharePoint = connections.find(c => c.provider === 'SHAREPOINT');
        this.loading = false;
        this.loadError = false;
        this.busy = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
        this.cdr.markForCheck();
      }
    });
  }

  private handleCallbackParams(): void {
    const params = this.route.snapshot.queryParamMap;
    if (params.get('datasource') !== 'sharepoint' && !params.get('datasourceError')) {
      return;
    }
    const consent = params.get('consent');
    const sessionId = params.get('siteSession');
    const connectionId = params.get('connection');
    const stateError = params.get('datasourceError');
    this.clearCallbackParams();

    if (stateError) {
      this.notifications.showErrorMessage(STATE_ERROR_TEXTS[stateError] ?? 'Die Rückmeldung von Microsoft war ungültig.');
    } else if (consent === 'connected') {
      this.notifications.showSuccessMessage('SharePoint ist verbunden. Als Nächstes Sites für planBIC freigeben.');
    } else if (consent === 'error') {
      this.notifications.showErrorMessage(consentErrorText(params.get('reason')));
    }
    if (sessionId && connectionId) {
      // Verbindungen mitladen: der Dialog braucht die aktuellen Freigaben, nicht den Stand vor dem Login.
      forkJoin({
        session: this.connectionService.getSitePermissionSession(this.organisationId, connectionId, sessionId),
        connections: this.connectionService.listExternalConnections(this.organisationId)
      }).subscribe({
        next: ({session, connections}) => {
          this.sharePoint = connections.find(c => c.provider === 'SHAREPOINT');
          this.continueSession(connectionId, session);
        },
        error: err => this.fail(err, 'Die Freigabe-Sitzung konnte nicht geladen werden.')
      });
    }
  }

  private continueSession(connectionId: string, session: SitePermissionSessionView): void {
    const Status = SitePermissionSessionView.StatusEnum;
    if (session.status === Status.AWAITINGSELECTION || session.status === Status.COMPLETEDWITHERRORS
      || session.status === Status.FAILED) {
      this.dialog.open(SitePermissionDialogComponent, {
        data: {organisationId: this.organisationId, connectionId, session, grantedSites: this.sharePoint?.sites ?? []},
        panelClass: 'rounded-corners-dialog'
      }).afterClosed().subscribe(() => this.load());
      return;
    }
    if (session.status === Status.COMPLETED) {
      this.notifications.showSuccessMessage(session.purpose === SitePermissionSessionView.PurposeEnum.DISCONNECT
        ? 'Die Verbindung zu SharePoint wurde getrennt.'
        : 'Die Sites wurden aktualisiert.');
      this.load();
    }
  }

  private clearCallbackParams(): void {
    this.router.navigate([], {relativeTo: this.route, queryParams: {}, replaceUrl: true});
  }

  private fail(err: { error?: { message?: string } }, fallback: string): void {
    this.busy = false;
    this.notifications.showErrorMessage(err?.error?.message ?? fallback);
    this.cdr.markForCheck();
  }

  /** Verlässt planBIC Richtung Microsoft-Login; eigene Methode, damit Tests die Weiterleitung abfangen können. */
  redirect(url: string): void {
    window.location.assign(url);
  }
}
